import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Globe, Building2, Calendar, Users, BarChart2, FileText,
  Leaf, ShieldCheck, TrendingUp, ExternalLink, ChevronDown, ChevronRight,
  BookOpen, Award, Check,
} from "lucide-react";
import type { AlignmentOutletContext } from "../AlignmentModule";
import WorkInProgress from "../WorkInProgress";
import griIcon from "../../../Assets/gri.png";
import "./ReferentielPage.css";

/* ─── Static GRI data ─── */

const GRI_KPIS = [
  { icon: Users,     value: "10 000+", label: "Organisations utilisatrices",         color: "#1a6b3c" },
  { icon: Globe,     value: "100+",    label: "Pays représentés",                    color: "#0369a1" },
  { icon: BarChart2, value: "78%",     label: "des entreprises G500 utilisent GRI",  color: "#7c3aed" },
  { icon: FileText,  value: "40+",     label: "Standards thématiques disponibles",   color: "#b45309" },
  { icon: Award,     value: "1997",    label: "Année de fondation",                  color: "#be123c" },
];

interface Standard {
  code: string;
  name: string;
  description: string;
}

interface ThemeGroup {
  theme: string;
  color: string;
  icon: React.ElementType;
  standards: Standard[];
}

const THEME_GROUPS: ThemeGroup[] = [
  {
    theme: "Standards Universels",
    color: "#1a6b3c",
    icon: BookOpen,
    standards: [
      { code: "GRI 1",  name: "Foundation 2021",         description: "Objectif et utilisation des standards GRI, principes de reporting." },
      { code: "GRI 2",  name: "General Disclosures 2021",description: "Informations sur l'organisation, sa gouvernance, sa stratégie et ses pratiques de reporting." },
      { code: "GRI 3",  name: "Material Topics 2021",    description: "Processus de détermination des sujets importants et leur gestion." },
    ],
  },
  {
    theme: "Environnement",
    color: "#16a34a",
    icon: Leaf,
    standards: [
      { code: "GRI 301", name: "Matières premières",        description: "Consommation de matières, matériaux recyclés et renouvelables." },
      { code: "GRI 302", name: "Énergie",                   description: "Consommation énergétique, efficacité et part renouvelable." },
      { code: "GRI 303", name: "Eau et effluents",          description: "Prélèvements, consommation, rejets et impacts sur les ressources en eau." },
      { code: "GRI 304", name: "Biodiversité",              description: "Impacts sur les habitats et espèces, zones protégées." },
      { code: "GRI 305", name: "Émissions",                 description: "GES scope 1, 2 et 3, intensité des émissions, objectifs climatiques." },
      { code: "GRI 306", name: "Déchets",                   description: "Production et gestion des déchets par type et destination." },
      { code: "GRI 308", name: "Évaluation fournisseurs (env.)", description: "Sélection des fournisseurs sur critères environnementaux." },
    ],
  },
  {
    theme: "Social",
    color: "#d97706",
    icon: Users,
    standards: [
      { code: "GRI 401", name: "Emploi",                    description: "Effectifs, embauches, départs, avantages sociaux." },
      { code: "GRI 403", name: "Santé et sécurité",         description: "Système SST, accidents, maladies professionnelles." },
      { code: "GRI 404", name: "Formation et éducation",    description: "Programmes de formation, développement des compétences." },
      { code: "GRI 405", name: "Diversité et égalité",      description: "Diversité des organes de gouvernance et des effectifs." },
      { code: "GRI 406", name: "Non-discrimination",        description: "Incidents de discrimination et mesures correctives." },
      { code: "GRI 408", name: "Travail des enfants",       description: "Risques dans les opérations et la chaîne d'approvisionnement." },
      { code: "GRI 409", name: "Travail forcé",             description: "Risques liés au travail forcé et due diligence." },
      { code: "GRI 413", name: "Communautés locales",       description: "Engagement, évaluation des impacts et programmes de développement." },
      { code: "GRI 414", name: "Évaluation fournisseurs (soc.)", description: "Évaluation sociale des fournisseurs et mesures correctives." },
    ],
  },
  {
    theme: "Gouvernance & Éthique",
    color: "#475569",
    icon: ShieldCheck,
    standards: [
      { code: "GRI 201", name: "Performance économique",    description: "Valeur économique générée et distribuée, risques climatiques financiers." },
      { code: "GRI 205", name: "Anticorruption",            description: "Évaluation des risques, politiques, formation, incidents confirmés." },
      { code: "GRI 206", name: "Comportement anticoncurrentiel", description: "Actions en justice liées aux pratiques anticoncurrentielles." },
    ],
  },
];

const GRI_PRINCIPLES = [
  { title: "Universalité",    desc: "Applicable à toute organisation quelle que soit sa taille, son secteur ou son pays." },
  { title: "Comparabilité",   desc: "Des indicateurs standardisés permettent la comparaison entre organisations et dans le temps." },
  { title: "Transparence",    desc: "Divulgation complète des sujets matériels, méthodes de mesure et périmètre de reporting." },
  { title: "Matérialité",     desc: "Focus sur les sujets qui ont les impacts les plus significatifs sur l'économie, l'environnement et les personnes." },
  { title: "Parties prenantes", desc: "Les attentes et intérêts des parties prenantes doivent guider le contenu du rapport." },
  { title: "Exactitude",      desc: "Les informations rapportées doivent être suffisamment précises et détaillées pour permettre une évaluation." },
];

const GRI_SOURCES = [
  {
    title: "GRI Standards — Site officiel",
    org: "Global Reporting Initiative",
    url: "https://www.globalreporting.org/standards/",
    desc: "Accès à l'ensemble des standards GRI téléchargeables gratuitement.",
    type: "Site web",
  },
  {
    title: "GRI 1 : Foundation 2021",
    org: "Global Reporting Initiative",
    url: "https://www.globalreporting.org/standards/media/3232/gri-1-foundation-2021.pdf",
    desc: "Standard fondateur définissant les objectifs et principes du cadre GRI.",
    type: "PDF",
  },
  {
    title: "GRI 2 : General Disclosures 2021",
    org: "Global Reporting Initiative",
    url: "https://www.globalreporting.org/standards/media/3223/gri-2-general-disclosures-2021.pdf",
    desc: "Informations contextuelles sur l'organisation et ses pratiques de reporting.",
    type: "PDF",
  },
  {
    title: "GRI 3 : Material Topics 2021",
    org: "Global Reporting Initiative",
    url: "https://www.globalreporting.org/standards/media/3220/gri-3-material-topics-2021.pdf",
    desc: "Méthodologie de détermination et de gestion des sujets matériels.",
    type: "PDF",
  },
  {
    title: "GRI 305 : Emissions 2016",
    org: "Global Reporting Initiative",
    url: "https://www.globalreporting.org/standards/media/1012/gri-305-emissions-2016.pdf",
    desc: "Reporting des émissions de GES scope 1, 2 et 3.",
    type: "PDF",
  },
  {
    title: "GRI & ISSB — Interoperability",
    org: "GRI & IFRS Foundation",
    url: "https://www.globalreporting.org/news/news-center/gri-and-issb-confirm-commitment-to-complementary-and-interconnected-reporting-standards/",
    desc: "Déclaration conjointe sur l'interopérabilité entre GRI et les normes ISSB/TCFD.",
    type: "Communiqué",
  },
];

/* ─── Sub-components ─── */

const ThemeSection: React.FC<{ group: ThemeGroup }> = ({ group }) => {
  const [open, setOpen] = useState(true);
  const Icon = group.icon;
  return (
    <div className="rp-theme-section">
      <button className="rp-theme-header" onClick={() => setOpen((o) => !o)}>
        <div className="rp-theme-header-left">
          <span className="rp-theme-dot" style={{ background: group.color }} />
          <Icon size={16} color={group.color} />
          <span className="rp-theme-title" style={{ color: group.color }}>{group.theme}</span>
          <span className="rp-theme-count">{group.standards.length} standards</span>
        </div>
        {open ? <ChevronDown size={16} color="#9ca3af" /> : <ChevronRight size={16} color="#9ca3af" />}
      </button>
      {open && (
        <div className="rp-standards-grid">
          {group.standards.map((s) => (
            <div key={s.code} className="rp-standard-card">
              <div className="rp-standard-code" style={{ color: group.color, borderColor: group.color + "33", background: group.color + "0f" }}>
                {s.code}
              </div>
              <div className="rp-standard-info">
                <span className="rp-standard-name">{s.name}</span>
                <span className="rp-standard-desc">{s.description}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Main GRI page ─── */

const GriReferentielContent: React.FC = () => (
  <div className="rp-page">

    {/* Hero */}
    <div className="rp-hero">
      <div className="rp-hero-left">
        <img src={griIcon} alt="GRI" className="rp-hero-logo" />
        <div>
          <h1 className="rp-hero-title">Global Reporting Initiative</h1>
          <p className="rp-hero-subtitle">
            Cadre de reporting développement durable le plus utilisé au monde — standard international
            permettant aux organisations de communiquer sur leurs impacts économiques, environnementaux et sociaux.
          </p>
          <div className="rp-hero-meta">
            <span><Calendar size={13} /> Fondé en 1997</span>
            <span><Building2 size={13} /> Amsterdam, Pays-Bas</span>
            <span><Globe size={13} /> Organisation indépendante</span>
          </div>
        </div>
      </div>
      <div className="rp-hero-badge">
        <Check size={14} />
        Référentiel implémenté
      </div>
    </div>

    {/* KPIs */}
    <div className="rp-kpis">
      {GRI_KPIS.map((kpi) => {
        const Icon = kpi.icon;
        return (
          <div key={kpi.label} className="rp-kpi-card">
            <div className="rp-kpi-icon" style={{ background: kpi.color + "14", color: kpi.color }}>
              <Icon size={20} />
            </div>
            <div className="rp-kpi-value" style={{ color: kpi.color }}>{kpi.value}</div>
            <div className="rp-kpi-label">{kpi.label}</div>
          </div>
        );
      })}
    </div>

    {/* Standards */}
    <section className="rp-section">
      <div className="rp-section-header">
        <TrendingUp size={18} color="#1a6b3c" />
        <h2>Structure des standards GRI</h2>
      </div>
      <div className="rp-themes">
        {THEME_GROUPS.map((g) => <ThemeSection key={g.theme} group={g} />)}
      </div>
    </section>

    {/* Principles */}
    <section className="rp-section">
      <div className="rp-section-header">
        <ShieldCheck size={18} color="#1a6b3c" />
        <h2>Principes de reporting</h2>
      </div>
      <div className="rp-principles-grid">
        {GRI_PRINCIPLES.map((p) => (
          <div key={p.title} className="rp-principle-card">
            <div className="rp-principle-check"><Check size={14} /></div>
            <div>
              <div className="rp-principle-title">{p.title}</div>
              <div className="rp-principle-desc">{p.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Sources */}
    <section className="rp-section rp-sources-section">
      <div className="rp-section-header">
        <FileText size={18} color="#1a6b3c" />
        <h2>Sources & Références officielles</h2>
      </div>
      <div className="rp-sources-list">
        {GRI_SOURCES.map((src) => (
          <div key={src.title} className="rp-source-card">
            <div className="rp-source-type-badge">{src.type}</div>
            <div className="rp-source-body">
              <div className="rp-source-title">{src.title}</div>
              <div className="rp-source-org">{src.org}</div>
              <div className="rp-source-desc">{src.desc}</div>
            </div>
            <a
              href={src.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rp-source-link"
              title="Ouvrir la source"
            >
              <ExternalLink size={15} />
            </a>
          </div>
        ))}
      </div>
      <p className="rp-sources-disclaimer">
        Les standards GRI sont disponibles gratuitement sur{" "}
        <a href="https://www.globalreporting.org" target="_blank" rel="noopener noreferrer">
          globalreporting.org
        </a>
        . Ils sont régulièrement mis à jour — vérifier la version en vigueur avant toute utilisation.
      </p>
    </section>
  </div>
);

/* ─── Route component ─── */

const ReferentielPage: React.FC = () => {
  const { selectedNorm } = useOutletContext<AlignmentOutletContext>();

  if (selectedNorm !== "GRI") {
    const labels: Record<string, string> = {
      CSRD: "CSRD — Corporate Sustainability Reporting Directive",
      VSME: "VSME — Voluntary SME Sustainability Standard",
      AMMC: "AMMC — Autorité Marocaine du Marché des Capitaux",
    };
    return <WorkInProgress normLabel={labels[selectedNorm] ?? selectedNorm} />;
  }

  return <GriReferentielContent />;
};

export default ReferentielPage;
